import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { apiGet } from '../lib/api'
import { formatAgo } from '../lib/format'
import { pagePath } from '../lib/pages'
import type { WlanAnsicht, WlanClient } from '../lib/types'

/** Ab diesem Signal gilt ein Client als schwach, wie im Agent (Stufe 3.4). */
export const SCHWACHES_SIGNAL_DBM = -75

/** Gruppiert die Clients nach einem Feld und zählt, größte Gruppe zuerst. */
export function zaehleNach(clients: WlanClient[], feld: (c: WlanClient) => string): [string, number][] {
  const n = new Map<string, number>()
  for (const c of clients) {
    const k = feld(c) || '–'
    n.set(k, (n.get(k) ?? 0) + 1)
  }
  return [...n.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'de'))
}

interface Props {
  deviceId: string
  customerSlug: string | null
}

/**
 * WLAN-Clients aus dem UniFi- oder Omada-Controller: am Controller alle, gruppiert nach
 * Access Point und SSID; an einem Access Point nur die eigenen. Ohne
 * Controller-Daten bleibt die Karte weg.
 */
export function WlanKarte({ deviceId, customerSlug }: Props) {
  const ansicht = useQuery({
    queryKey: ['device-wlan', deviceId],
    queryFn: ({ signal }) => apiGet<WlanAnsicht>(`/devices/${deviceId}/wlan`, signal),
    refetchInterval: 60_000,
  })
  const [filter, setFilter] = useState('')
  const d = ansicht.data
  const clients = useMemo(() => (d && Array.isArray(d.clients) ? d.clients : []), [d])
  const gefiltert = useMemo(() => {
    const f = filter.trim().toLowerCase()
    if (!f) return clients
    return clients.filter((c) => [c.name, c.mac, c.ip, c.ssid, c.ap].some((v) => (v ?? '').toLowerCase().includes(f)))
  }, [clients, filter])

  // Eine Antwort ohne die erwartete Form lässt die Karte weg, statt die
  // Geräteseite mitzureißen; ohne Controller-Daten gibt es nichts zu zeigen.
  if (!d || !Array.isArray(d.clients) || !d.quelle) return null

  const amController = d.quelle === 'controller'
  const schwach = clients.filter((c) => c.signal !== undefined && Number(c.signal) <= SCHWACHES_SIGNAL_DBM).length

  return (
    <section className="card" aria-labelledby="wlan-titel">
      <div className="card-head" style={{ flexWrap: 'wrap', gap: 12 }}>
        <h2 className="card-title" id="wlan-titel">
          WLAN-Clients
        </h2>
        <span className="muted" style={{ fontSize: 12 }}>
          {clients.length} verbunden
          {schwach > 0 ? ` · ${schwach} mit schwachem Signal` : ''}
          {d.stand ? ` · Stand ${formatAgo(d.stand)}` : ''}
          {!amController && d.controller_id ? (
            <>
              {' · laut '}
              <Link to={`${pagePath('geraete', customerSlug)}/${d.controller_id}`}>{d.controller_name}</Link>
            </>
          ) : null}
        </span>
        {clients.length > 8 ? (
          <div className="field" style={{ marginLeft: 'auto' }}>
            <label htmlFor="wlan-filter" className="sr-only">
              Clients filtern
            </label>
            <input
              id="wlan-filter"
              className="input"
              placeholder="Name, MAC, IP, SSID …"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        ) : null}
      </div>

      {clients.length === 0 ? (
        <div className="card-body muted">Gerade ist kein WLAN-Client verbunden.</div>
      ) : (
        <>
          <div className="topliste-raster">
            {amController ? <Gruppen titel="Je Access Point" zeilen={zaehleNach(clients, (c) => c.ap ?? '')} /> : null}
            <Gruppen titel="Je SSID" zeilen={zaehleNach(clients, (c) => c.ssid ?? '')} />
            <Gruppen titel="Je Band" zeilen={zaehleNach(clients, (c) => c.band ?? '')} />
          </div>
          <table className="data" aria-label="WLAN-Clients">
            <thead>
              <tr>
                <th>Client</th>
                <th>IP</th>
                <th>SSID</th>
                {amController ? <th>Access Point</th> : null}
                <th>Band</th>
                <th style={{ textAlign: 'right' }}>Signal</th>
                <th style={{ textAlign: 'right' }}>Qualität</th>
              </tr>
            </thead>
            <tbody>
              {gefiltert.slice(0, 500).map((c) => {
                const signal = c.signal !== undefined ? Number(c.signal) : null
                return (
                  <tr key={c.mac}>
                    <td>
                      {c.name || <span className="muted">ohne Namen</span>}
                      <div className="mono muted" style={{ fontSize: 12 }}>
                        {c.mac}
                      </div>
                    </td>
                    <td className="mono">{c.ip || '–'}</td>
                    <td>{c.ssid || '–'}</td>
                    {amController ? <td>{c.ap || '–'}</td> : null}
                    <td>
                      {c.band || '–'}
                      {c.channel ? <span className="muted"> · Kanal {c.channel}</span> : null}
                    </td>
                    <td
                      className="mono"
                      style={{
                        textAlign: 'right',
                        color: signal !== null && signal <= SCHWACHES_SIGNAL_DBM ? 'var(--warn)' : undefined,
                      }}
                    >
                      {signal !== null ? `${signal} dBm` : '–'}
                    </td>
                    <td className="mono" style={{ textAlign: 'right' }}>
                      {c.satisfaction ? `${c.satisfaction} %` : '–'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {gefiltert.length > 500 ? (
            <div className="card-body muted" style={{ fontSize: 12 }}>
              Die ersten 500 von {gefiltert.length} Clients; der Filter grenzt ein.
            </div>
          ) : null}
        </>
      )}
    </section>
  )
}

function Gruppen({ titel, zeilen }: { titel: string; zeilen: [string, number][] }) {
  return (
    <table className="data" aria-label={titel}>
      <thead>
        <tr>
          <th>{titel}</th>
          <th style={{ textAlign: 'right' }}>Clients</th>
        </tr>
      </thead>
      <tbody>
        {zeilen.slice(0, 10).map(([name, n]) => (
          <tr key={name}>
            <td>{name}</td>
            <td className="mono" style={{ textAlign: 'right' }}>
              {n}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
